import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import type { WaterExceptionPeriod } from '@models/water-period';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { finalize } from 'rxjs';
import { isSeasonPeriodRowValid, SeasonPeriodRow, SeasonPeriodTableComponent } from './season-period-table.component';

/** Number of future years selectable beyond the current year (matches legacy MAX_YEARS_AHEAD). */
const YEARS_AHEAD = 3;

/**
 * The "Exceptions" tab: season periods that override the recurring defaults for one specific year.
 * A year selector scopes the editable table; saving replaces only the selected year's exceptions.
 */
@Component({
  selector: 'app-season-periods-exceptions-tab',
  templateUrl: './season-periods-exceptions-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    SeasonPeriodTableComponent,
  ],
})
export class SeasonPeriodsExceptionsTabComponent implements OnInit {
  readonly waterId = input.required<number>();
  /** Emits the exception count for the selected year so the parent can show it as a tab badge. */
  readonly count = output<number>();

  private readonly periodsService = inject(CrmWaterPeriodsService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly rows = signal<SeasonPeriodRow[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);

  /**
   * Every row must have a season selected before saving, and must not run backwards: an exception is
   * pinned to one year, so a wrapping range there would mean two disjoint arcs rather than a winter.
   * Wrapping is allowed on the Periods tab, not here.
   */
  readonly isValid = computed(() => this.rows().every((row) => isSeasonPeriodRowValid(row, false)));
  /** Set when Save is clicked with invalid rows; cleared as soon as the rows are edited. */
  readonly showValidationError = signal(false);

  onRowsChange(rows: SeasonPeriodRow[]): void {
    this.rows.set(rows);
    this.showValidationError.set(false);
  }

  private readonly currentYear = new Date().getFullYear();
  readonly selectedYear = signal(this.currentYear);
  /** Years already holding exception data, merged with current..+3 so nothing is hidden. */
  private readonly existingYears = signal<number[]>([]);

  readonly yearOptions = computed(() => {
    const years = new Set<number>(this.existingYears());
    for (let y = this.currentYear; y <= this.currentYear + YEARS_AHEAD; y++) years.add(y);
    years.add(this.selectedYear());
    return [...years].sort((a, b) => a - b);
  });

  constructor() {
    effect(() => this.count.emit(this.rows().length));
  }

  ngOnInit(): void {
    this.periodsService
      .getExceptionYears(this.waterId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => [] as number[],
          this.translate.instant('waters.season-periods.load-failed'),
        ),
      )
      .subscribe((years) => this.existingYears.set(years));
    this.load();
  }

  onYearChange(year: number): void {
    this.selectedYear.set(year);
    this.showValidationError.set(false);
    this.load();
  }

  private load(): void {
    this.isLoading.set(true);
    this.periodsService
      .getExceptionPeriods(this.waterId(), this.selectedYear())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => [] as WaterExceptionPeriod[],
          this.translate.instant('waters.season-periods.load-failed'),
        ),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((periods) => this.rows.set(periods.map(toRow)));
  }

  save(): void {
    if (!this.isValid()) {
      this.showValidationError.set(true);
      return;
    }
    const year = this.selectedYear();
    this.isSaving.set(true);
    this.periodsService
      .saveExceptionPeriods(this.waterId(), year, { periods: this.rows().map((r) => toExceptionPeriod(r, year)) })
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (periods) => {
          this.rows.set(periods.map(toRow));
          // Keep the tracked years in sync with what the backend now holds: add the year when it has
          // data, drop it when cleared. The current…+3 range and the selected year always stay
          // selectable via yearOptions, so a just-cleared year doesn't vanish mid-edit.
          this.existingYears.update((years) => {
            const has = years.includes(year);
            if (periods.length > 0 && !has) return [...years, year];
            if (periods.length === 0 && has) return years.filter((y) => y !== year);
            return years;
          });
          this.toastService.showSuccess(this.translate.instant('waters.season-periods.save-success'));
        },
        error: () => this.toastService.showError(this.translate.instant('waters.season-periods.save-failed')),
      });
  }
}

function toRow(p: WaterExceptionPeriod): SeasonPeriodRow {
  return {
    id: p.id,
    periodTypeId: p.periodTypeId,
    fromWeek: p.fromWeek,
    fromDay: p.fromDay,
    untilWeek: p.untilWeek,
    untilDay: p.untilDay,
    clientId: crypto.randomUUID(),
  };
}

function toExceptionPeriod(r: SeasonPeriodRow, year: number): WaterExceptionPeriod {
  return {
    id: r.id,
    periodTypeId: r.periodTypeId,
    fromWeek: r.fromWeek,
    fromDay: r.fromDay,
    untilWeek: r.untilWeek,
    untilDay: r.untilDay,
    exceptionYear: year,
  };
}
